"use client";

import { Button, CircularProgress, Divider } from "@mui/material";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useStore } from "@nanostores/react";
import AdminClient from "common/clients/AdminClient";
import AuthClient from "common/clients/AuthClient";
import SimpleAutocomplete, {
	SimpleAutocompleteOption,
} from "common/components/FormSelect/SimpleAutoComplete";
import {
	clearSession,
	hasWriteUploadData,
	isOpteraAdmin,
	user,
} from "common/stores/UserStore";
import { logoutRedirect } from "common/utils/auth/AuthUtils";
import { Organization } from "common/utils/OrganizationUtils";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { SyntheticEvent, useEffect, useState } from "react";
import useSWR from "swr";
import BinaryModal from "./BinaryModal";

interface AvatarMenuProps {
	setAnchorEl: (arg1: unknown) => void;
	organization: string;
	anchorEl: null | HTMLElement;
}

export default function AvatarMenu({
	setAnchorEl,
	organization,
	anchorEl,
}: AvatarMenuProps) {
	const router = useRouter();
	const pathname = usePathname();
	const $user = useStore(user);
	const [activeModal, setActiveModal] = useState<"" | "changeOrg">("");
	const hideModal = () => setActiveModal("");
	const [changeOrgModal, setChangeOrgModal] = useState(null);
	const [isNavigatingToPreferences, setIsNavigatingToPreferences] =
		useState(false);

	const preferencesPath = isOpteraAdmin()
		? "/operator/admin/preferences"
		: "/operator/preferences";

	useEffect(() => {
		if (pathname === preferencesPath) {
			setIsNavigatingToPreferences(false);
			setAnchorEl(null);
		}
	}, [pathname, preferencesPath, setAnchorEl]);

	const handlePreferencesClick = () => {
		setIsNavigatingToPreferences(true);
		router.push(preferencesPath);
	};

	let orgs: SimpleAutocompleteOption[] = [{ label: "Loading...", value: "" }];
	let currentOrgOption: SimpleAutocompleteOption = {
		label: organization ?? "Loading",
		value: organization ?? "Loading",
	};

	const isAdmin = $user.roles.includes("write:organization");
	const apiKeyFeatureAccess =
		$user.roles.includes("read:apikey") && $user.roles.includes("write:apikey");
	const isMultiOrgAdmin = $user.roles.includes("multiorg:admin");
	const enableOrgPreferences = isOpteraAdmin() || hasWriteUploadData();

	let currentOrgName: string =
		isAdmin || isMultiOrgAdmin ? "Loading" : $user.organization;

	const formatOrgs = (respOrgs: Organization[]) => {
		if (!respOrgs?.length) return;
		// get sorted list of unique orgs
		const sortedOrgs = [
			...new Map(
				respOrgs.map((item) => [
					item.name,
					{ label: item.name, value: String(item.id) },
				])
			).values(),
		].sort((a, b) =>
			a.label.localeCompare(b.label)
		) as SimpleAutocompleteOption[];
		const selectedOrg = sortedOrgs.find(
			(org) => org.value === String($user.organization_id)
		);
		// Update psuedo-state variables
		currentOrgName = selectedOrg?.label;
		currentOrgOption = selectedOrg ?? currentOrgOption;
		orgs = sortedOrgs;
	};

	// org list is only relevant if role is optera admin || multiorg admin
	const { data, error } = useSWR(
		isAdmin || isMultiOrgAdmin ? "/auth/my-organizations" : null,
		() => AdminClient.getMyOrganizations(),
		{
			shouldRetryOnError: false,
			revalidateOnFocus: false,
			dedupingInterval: 3600000,
		}
	);

	if (data ?? data?.length) {
		formatOrgs(data);
	}

	if (error) {
		console.error(error);
	}

	const handleOrgChange = (
		_event: SyntheticEvent,
		selectedOrg: SimpleAutocompleteOption
	) => {
		if (!selectedOrg) return;
		const prevOrg = currentOrgName;

		setChangeOrgModal({
			headerText: "Are you sure you want to login to a different organization?",
			bodyText: `You have indicated you want to logout of ${prevOrg} and login to ${selectedOrg.label}.`,
			primaryButtonText: "Yes",
			primaryButtonAction: () => {
				AuthClient.changeOrg(selectedOrg.value);
			},
			secondaryButtonText: "Cancel",
			secondaryButtonAction: hideModal,
		});
		setActiveModal("changeOrg");
	};

	return (
		<Menu
			anchorEl={anchorEl}
			open={Boolean(anchorEl)}
			onClose={() => setAnchorEl(null)}
			id="account-menu"
			slotProps={{
				paper: {
					elevation: 0,
					sx: {
						overflow: "visible",
						filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
						mt: 1.5,
						"& .MuiAvatar-root": {
							width: 32,
							height: 32,
							ml: -0.5,
							mr: 1,
						},
						p: 3,
						width: "450px",
						maxHeight: "None",
					},
				},
			}}
			transformOrigin={{ horizontal: "right", vertical: "top" }}
			anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
		>
			<MenuItem
				key="avatarMenuItem-orgLabel"
				disableRipple
				sx={{
					"&:hover": { backgroundColor: "transparent" },
					cursor: "default",
				}}
				onKeyDown={(e) => e.stopPropagation()}
			>
				<div className="flex-col gap-2 pb-2" style={{ width: "100%" }}>
					<p className="subtitle2">My Organization</p>
					{isAdmin || isMultiOrgAdmin ? (
						<SimpleAutocomplete
							options={orgs}
							value={currentOrgOption}
							onChange={handleOrgChange}
							name="org-select"
							placeholder="Search organizations"
							showClearButton
						/>
					) : (
						<p className="body1">{currentOrgName}</p>
					)}
				</div>
			</MenuItem>
			{isAdmin && (
				<div>
					<Link
						href="/admin/organizations"
						key="avatarMenuItem-userManagement"
						prefetch={false}
					>
						<MenuItem>
							<div className="pb-2">
								<p className="body1">Organization Management</p>
								<span className="caption text-secondary">
									Create, Edit, and Delete Organizations
								</span>
							</div>
						</MenuItem>
					</Link>
					<Link
						href="/admin/logs/org-logs"
						key="avatarMenuItem-logs"
						prefetch={false}
					>
						<MenuItem>
							<div className="pb-2">
								<p className="body1">Logs</p>
								<span className="caption text-secondary">
									View user and org logs
								</span>
							</div>
						</MenuItem>
					</Link>
					{apiKeyFeatureAccess && (
						<Link
							href="/admin/api-keys"
							key="avatarMenuItem-api-keys"
							prefetch={false}
						>
							<MenuItem>
								<div className="pb-2">
									<p className="body1">API Key Management</p>
									<span className="caption text-secondary">
										Manage API keys
									</span>
								</div>
							</MenuItem>
						</Link>
					)}
				</div>
			)}
			<Divider />
			<div className="flex-row gap-2 mt-2">
				<Button
					color="secondary"
					variant="outlined"
					onClick={(e) => {
						e.preventDefault();
						clearSession(() => {
							logoutRedirect("/login", "/");
						});
					}}
				>
					Logout
				</Button>
				{enableOrgPreferences && (
					<Button
						color="secondary"
						variant="outlined"
						onClick={handlePreferencesClick}
						disabled={isNavigatingToPreferences}
						endIcon={
							isNavigatingToPreferences ? (
								<CircularProgress size={16} color="inherit" />
							) : null
						}
					>
						Preferences
					</Button>
				)}
			</div>
			<BinaryModal
				openModal={activeModal === "changeOrg"}
				{...changeOrgModal}
			/>
		</Menu>
	);
}
